import React, { useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Collapse from "@mui/material/Collapse";
import CardActions from "@mui/material/CardActions";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { MdExpandMore, MdClose } from "react-icons/md";

import Flash from "../../layout/Flash";
import Messages from "../Messages";
import StatusPrompts from "./prompts/StatusPrompts";
import CprCard from "./CprCard";
import { FeedPrompt } from "../../Contexts";
import * as API from "../../api";
import * as CONST from "../../Constants";

const propTypes = {
  cpr: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  flash: PropTypes.func,
  window: () => Window,
  children: React.createElement,
  notifications: PropTypes.object,
};

CprStatusItem.prototype = propTypes;

const ExpandPrompt = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
}));
export default function CprStatusItem(props) {
  const [cprData, setCprData] = React.useState(props.cpr);
  const [promptActive, setPromptActive] = useState(false);
  const [feedPrompt, setFeedPrompt] = useState(null);
  const [expand, setExpanded] = useState(false);
  const [hidePrompts, setHidePrompts] = useState(false);
  const [flash, setFlash] = useState(null);
  const location = useLocation();

  const providerFeedPrompt = React.useMemo(
    () => ({ ...feedPrompt, setFeedPrompt }),
    [feedPrompt, setFeedPrompt]
  );

  React.useEffect(() => {
    setCprData(props.cpr);
    if (feedPrompt) {
      props.scrollToView(`#scroll-to-prompt-${cprData.id}`, 400);
    }
  }, [props.cpr, props.notifications]);

  function updateCurrentCpr(payload) {
    if (!payload.updatedAppointmentDate && !payload.updatedDueDate) {
      setFeedPrompt(null);
      setExpanded(!expand);
      location.pathname.includes("dashboard") &&
        props.scrollToView(`#scroll-to-prompt-${cprData.id}`, 300);
    }
  }

  const renderStatusPrompts = (
    <StatusPrompts
      cpr={cprData}
      promptActive={() => {
        if (!location.pathname.includes("dashboard")) {
          setPromptActive(!promptActive);
        }
        props.scrollToView(`#scroll-to-prompt-${cprData.id}`, 300);
      }}
      dispatch={props.dispatch}
      hidePrompts={hidePrompts}
      flash={(message) => props.flash(message)}
      updateProgress={(payload) => updateCurrentCpr(payload)}
    />
  );

  const renderMessages = (
    <Messages
      showMessages={feedPrompt && feedPrompt.type == "message"}
      cpr={cprData}
      dispatch={props.dispatch}
      messages={cprData.task_messages}
      cprId={cprData.id}
    />
  );

  const renderPagePrompt = (
    <>
      <Collapse in={expand} timeout="auto" unmountOnExit id={`scroll-to-prompt-${props.cpr.id}`}>
        {feedPrompt && (props.cpr.outcome_message == null) && renderStatusPrompts}
        {feedPrompt &&
          feedPrompt.type == "message" &&
          !props.cpr.task_messages.length > 0 &&
          renderMessages}
      </Collapse>
      {!!props.cpr.task_messages.length > 0 && renderMessages}
    </>
  );
  const renderFeedPrompt = (
    <Collapse in={expand} timeout="auto" unmountOnExit id={`scroll-to-prompt-${props.cpr.id}`}>
      {feedPrompt &&  (props.cpr.outcome_message == null) && renderStatusPrompts}
      {feedPrompt && feedPrompt.type == "message" && renderMessages}
    </Collapse>
  );

  const notificationsPrompt = (
    <>
      {(props.cpr.outcome_message == null) && renderStatusPrompts}
      {renderMessages}
    </>
  );
  const renderExpandPanel = (
    <CardActions disableSpacing className="bt">
      {cprData.updatedAppointmentDate || cprData.updatedDueDate ? null : (
        <ExpandPrompt
          className="card-button"
          expand={expand}
          onClick={() => {
            props.scrollToView(`#scroll-to-prompt-${cprData.id}`, 300);
            setExpanded(false);
          }}
          aria-expanded={expand}
          aria-label="expand cpr status"
        >
          {expand ? <MdClose /> : <MdExpandMore />}
        </ExpandPrompt>
      )}
    </CardActions>
  );
  return (
    <FeedPrompt.Provider value={providerFeedPrompt}>
      {flash && <Flash flash={flash} handleClose={(e) => setFlash(null)} showButton={false} />}
      {cprData && (
        <CprCard
          dispatch={props.dispatch}
          cpr={cprData}
          prompt={{
            active: expand,
            handler: async (prompt) => {
              setFeedPrompt({ type: prompt });
              props.dispatch({ type: "set_current_cpr", payload: cprData });
              if (prompt == "order") {
                const res = await API.postCprStatus({ notification_id: cprData.current_notification_id, cpr_id: cprData.id, option_value: 6 });
                console.log(res);
                props.dispatch({ type: "set_current_cpr", payload: res.data.cpr });
                if (res.error) {
                  setFlash({ error: res.error });
                } else {
                  setFlash({
                    success: CONST.ORDER_REQUESTED_TEXT,
                  });
                }
              } else {
                !expand && setExpanded(true);
                props.scrollToView(`#scroll-to-prompt-${cprData.id}`, 100);
              }
            },
          }}
          expandPanel={expand && renderExpandPanel}
          children={
            location.pathname.includes("notifications")
              ? notificationsPrompt
              : location.pathname.includes("dashboard")
              ? renderFeedPrompt
              : renderPagePrompt
          }
        />
      )}
    </FeedPrompt.Provider>
  );
}
